import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, Plus, Shapes, Sparkles, SquarePen, X } from 'lucide-react';
import { useTenant } from '../../auth/TenantContext';
import { useDismissable } from '../../lib/useDismissable';
import { cn } from '../../lib/cn';
import { MenuItem, MenuScrim, chromeFocusRing, chromeMenuPanel } from './MenuItem';
import { useShellMenu } from './useShellMenu';

/**
 * Where New Content sits: at the top of the sidebar (`lg` up), at the top of the tablet rail (`md` to
 * `lg`), or raised in the middle of the bottom bar (below `md`). Each is its own instance with its own
 * open state, and CSS shows one per width.
 */
export type CreateContentPlacement = 'sidebar' | 'rail' | 'bar';

/**
 * The New Content button and its menu: a new post, or generated media or a design, opened over the
 * current page (no navigation). In the sidebar it is the labelled split button; in the rail and the
 * bottom bar it is a plus with the same accessible name, which turns into a close (x) while its menu is
 * open over the dimmed page.
 *
 * Rendered only once the user has a Brand Workspace, as it always was: the three things it opens are
 * mounted only then.
 */
export function CreateContentButton({ placement }: { placement: CreateContentPlacement }) {
  const { hasWorkspace } = useTenant();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);
  useDismissable(open, ref, close);
  useShellMenu(open, close, ref);

  // Escape closes this menu and nothing else. The sidebar stays usable while the composer is open, and
  // the composer closes on an Escape that reaches `document`, throwing away the post: so while the menu
  // is open it takes the key first (capture phase) and stops it there, as ui/Modal does.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, close]);

  if (!hasWorkspace) return null;

  // Each entry sets one search param over the current page and replaces the history entry.
  const openOver = (key: 'compose' | 'generate', value: string) => {
    setOpen(false);
    const next = new URLSearchParams(params);
    next.set(key, value);
    // A new post opens on its first tab: the tab param belongs to a post that may be open now
    // (ComposerModal.openSibling drops it for the same reason). A page's campaign and title stay.
    if (key === 'compose') next.delete('ctab');
    setParams(next, { replace: true });
  };

  const sidebar = placement === 'sidebar';
  const Glyph = open ? X : Plus;

  return (
    <div ref={ref} className={cn('relative', sidebar && 'mb-4', placement === 'rail' && 'mb-[calc(12*var(--nav-px))] shrink-0')}>
      {sidebar ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          className={cn(
            'flex h-11 w-full items-center gap-2.5 rounded-[0.625rem] border border-[#18181B] bg-create pl-4 pr-3.5 text-sm font-semibold text-white transition hover:brightness-110',
            chromeFocusRing,
          )}
        >
          <Plus className="h-4 w-4 shrink-0" aria-hidden />
          <span className="flex-1 text-left">New Content</span>
          <span className="h-5 w-px shrink-0 bg-white/25" aria-hidden />
          <ChevronDown
            className={cn('h-3.5 w-3.5 shrink-0 transition-transform', open && 'rotate-180')}
            aria-hidden
          />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="New Content"
          className={cn(
            'relative z-10 flex items-center justify-center bg-create text-white transition hover:brightness-110',
            placement === 'rail'
              ? // 60x48 in the rail's exact px (`--nav-px`, NavRail).
                'h-[calc(48*var(--nav-px))] w-[calc(60*var(--nav-px))] rounded-[calc(12*var(--nav-px))]'
              : // Raised 18px out of the bar, ringed in the page colour so it stands off what scrolls under it.
                '-mt-[1.125rem] size-14 rounded-full border-4 border-page',
            chromeFocusRing,
          )}
        >
          <Glyph
            className={
              placement === 'rail'
                ? 'h-[calc(24*var(--nav-px))] w-[calc(24*var(--nav-px))]'
                : 'h-[1.625rem] w-[1.625rem]'
            }
            aria-hidden
          />
        </button>
      )}
      {open && (
        <div
          role="menu"
          aria-label="Create new"
          className={cn(
            'absolute z-20 overflow-hidden py-1',
            // In the sidebar the menu is the button's width, so it stays inside the pane. From the rail it
            // opens to the right, level with the button's top; from the bar, centred above the button.
            placement === 'sidebar' && 'left-0 right-0 mt-2',
            placement === 'rail' && 'left-full top-0 ml-3 w-[16.25rem]',
            // Centred by a margin, not a translate: the opening animation moves it with `transform`.
            placement === 'bar' && 'bottom-full left-1/2 -ml-[7.8125rem] mb-3 w-[15.625rem]',
            chromeMenuPanel,
          )}
        >
          <MenuItem icon={<SquarePen className="h-4 w-4" />} onClick={() => openOver('compose', 'new')}>
            New post
          </MenuItem>
          <MenuItem icon={<Sparkles className="h-4 w-4" />} onClick={() => openOver('generate', 'media')}>
            Generate media
          </MenuItem>
          {/* One `generate` param serves media and design, so the two modals are mutually exclusive
              without any coordination between them. */}
          <MenuItem icon={<Shapes className="h-4 w-4" />} onClick={() => openOver('generate', 'design')}>
            Generate design
          </MenuItem>
        </div>
      )}
      {/* Under the button and its menu (z-10, z-20): the button stays lit and is how the menu is closed. */}
      {open && !sidebar && <MenuScrim onDismiss={close} className="z-[5]" />}
    </div>
  );
}
